import { createDeliverySetupSteps } from "@/components/campaign/setup/delivery/steps";
import { AlertModal } from "@/components/generic/AlertModal";
import {
  HorizontalStepperSteps,
  StepperBottomBar,
} from "@/components/StepperSteps";
import { PageHeaderProvider } from "@/providers/PageHeaderProvider";
import { NewCampaignStateDelivery } from "@/services/new-campaign.service";
import { DeferredPromise } from "@/types/deferred-promise";
import { store } from "@/utils/jotai";
import { createStepper } from "@/utils/stepper";
import {
  createFileRoute,
  Outlet,
  useBlocker,
  useMatches,
  useNavigate,
} from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import { atom, useAtom, useSetAtom } from "jotai";
import { useCallback, useMemo } from "react";
import z from "zod";

const stepperSymbol = Symbol();

const deliverySetupAtom = atom<{
  campaignId: number;
  state: NewCampaignStateDelivery;
}>();

const confirmDeliveryAtom = atom<DeferredPromise<boolean> | null>(null);

const historyStateSchema = z
  .object({
    autoSubmit: z.boolean(),
  })
  .optional()
  .catch(undefined);

function parseHistoryState(historyState: z.infer<typeof historyStateSchema>) {
  return historyStateSchema.parse(historyState);
}

export const Route = createFileRoute(
  "/_dashboard/campaigns/$id/(edit)/delivery/setup",
)({
  component: RouteComponent,
  staticData: {
    meta: {
      title: "Skift levering",
    },
  },
  beforeLoad: ({ context, params, matches, location }) => {
    let deliverySetupState = store.get(deliverySetupAtom);
    if (deliverySetupState?.campaignId !== params.id) {
      deliverySetupState = {
        campaignId: params.id,
        state: context.stepperContext.delivery,
      };
      store.set(deliverySetupAtom, deliverySetupState);
    }

    const useDeliverySetupState = () => {
      const [_deliverySetupState, _setDeliverySetupState] =
        useAtom(deliverySetupAtom);

      const deliverySetupState = useMemo(
        () =>
          _deliverySetupState?.campaignId === params.id
            ? _deliverySetupState.state
            : context.stepperContext.delivery,
        [_deliverySetupState],
      );

      const setDeliverySetupState = useCallback(
        (
          action: (
            prevDeliverySetupState: NewCampaignStateDelivery,
          ) => NewCampaignStateDelivery,
        ) =>
          _setDeliverySetupState((prevDeliverySetupState) => {
            if (prevDeliverySetupState?.campaignId !== params.id)
              return prevDeliverySetupState;
            return {
              campaignId: params.id,
              state: action(prevDeliverySetupState.state),
            };
          }),
        [_setDeliverySetupState],
      );
      return [deliverySetupState, setDeliverySetupState] as const;
    };

    const deliveryStepper = createStepper({
      symbol: stepperSymbol,
      context: {
        configurations: context.stepperContext.configurations,
        delivery: deliverySetupState.state,
      },
      steps: createDeliverySetupSteps({
        method: {
          id: "method",
          to: "/campaigns/$id/delivery/setup/method",
        },
        configure: {
          id: "configure",
          to: "/campaigns/$id/delivery/setup/configure",
        },
        "integration/select": {
          id: "integration/select",
          to: "/campaigns/$id/delivery/setup/integration/select",
        },
        "integration/method": {
          id: "integration/method",
          to: "/campaigns/$id/delivery/setup/integration/method",
        },
        "integration/configure": {
          id: "integration/configure",
          to: "/campaigns/$id/delivery/setup/integration/configure",
        },
      }),
    });

    deliveryStepper.onBeforeLoadStepRoute(Route.fullPath, matches);

    const useUpdateCampaignDelivery = () => {
      const navigate = useNavigate();

      const setConfirmDelivery = useSetAtom(confirmDeliveryAtom);

      const { updateCampaign, updateCampaignMutation } =
        context.useEditCampaign();

      const confirmUpdate = useCallback(() => {
        const deferred = new DeferredPromise<boolean>();
        setConfirmDelivery((prev) => {
          prev?.resolve(false);
          return deferred;
        });
        return deferred.promise;
      }, [setConfirmDelivery]);

      const updateCampaignDelivery = useCallback(
        async (delivery: NewCampaignStateDelivery) => {
          switch (delivery.type) {
            case "LassoSales":
              await updateCampaign({
                completionEmail: "",
                removeIntegration: true,
                deliverContacts: false,
                deliverContactsRequirePhone: false,
                deliverLiveNumber: false,
              });
              break;

            case "Excel":
              await updateCampaign({
                removeIntegration: true,
                completionEmail: delivery.completionEmail,
              });
              break;

            case "Integration":
              await updateCampaign({
                completionEmail: "",
                exportSystem: delivery.exportSystem,
                exportMode: delivery.exportMode,
                exportInput: delivery.exportInput,
              });
              break;
          }

          await navigate({
            from: Route.fullPath,
            to: "/campaigns/$id/delivery",
            ignoreBlocker: true,
          });
        },
        [navigate, updateCampaign],
      );

      return useMemo(
        () => ({
          updateCampaignDelivery,
          updateCampaignMutation,
          confirmUpdate,
        }),
        [confirmUpdate, updateCampaignDelivery, updateCampaignMutation],
      );
    };

    const historyState = parseHistoryState(
      location.state.routeStates?.[Route.id],
    );

    return {
      parseHistoryState,
      useHistoryState: () => historyState,
      deliveryStepper,
      useDeliveryStepper: () => {
        const { editCampaign } = context.useEditCampaign();

        const [deliverySetupState, setDeliverySetupState] =
          useDeliverySetupState();

        const stepperContext = useMemo(
          () => ({
            configurations: editCampaign.configurations,
            delivery: deliverySetupState,
          }),
          [deliverySetupState, editCampaign.configurations],
        );

        return deliveryStepper.useStepper({
          context: stepperContext,
          setContext: (action) =>
            setDeliverySetupState((prevDeliverySetupState) => {
              const { delivery: newDeliverySetupState } = action({
                ...stepperContext,
                delivery: prevDeliverySetupState,
              });

              return newDeliverySetupState;
            }),
        });
      },
      useUpdateCampaignDelivery,
    };
  },
  onLeave: () => {
    store.set(deliverySetupAtom, undefined);
    store.set(confirmDeliveryAtom, (prev) => {
      prev?.resolve(false);
      return null;
    });
  },
});

function RouteComponent() {
  const context = Route.useRouteContext();
  const stepper = context.useDeliveryStepper();

  const matches = useMatches();

  const hideSubStepperSteps = useMemo(
    () => stepper.visibleSteps.length <= 1,
    [stepper.visibleSteps.length],
  );

  const { showSubStepper, showBottomBar, hideSubmitButton } = useMemo(() => {
    let showSubStepper = true;
    let showBottomBar = true;
    let hideSubmitButton = false;

    const schema = z
      .object({
        showCampaignSetupSubStepper: z.boolean().catch(true),
        showCampaignSetupBottomBar: z.boolean().catch(true),
        hideSubmitButton: z.boolean().catch(false),
      })
      .optional();

    for (const match of [...matches].reverse()) {
      for (const rawData of [match.loaderData, match.staticData]) {
        if (!showSubStepper && !showBottomBar) break;

        const data = schema.parse(rawData);
        if (!data) continue;

        if (!data.showCampaignSetupSubStepper) showSubStepper = false;
        if (!data.showCampaignSetupBottomBar) showBottomBar = false;
        if (data.hideSubmitButton) hideSubmitButton = true;
      }

      if (!showSubStepper && !showBottomBar) break;
    }

    return { showSubStepper, showBottomBar, hideSubmitButton };
  }, [matches]);

  const [confirmState, setConfirmState] = useAtom(confirmDeliveryAtom);

  const blocker = useBlocker({
    // TODO - Maybe this should be conditional based on the form states, but, the way I see it. If you're inside the setup routes, you would be in a dirty state that we should warn users about leaving
    shouldBlockFn: ({ next }) => {
      if (next.routeId.startsWith(Route.id)) return false;
      return true;
    },
    withResolver: true,
  });

  return (
    <PageHeaderProvider
      options={{
        backLinkOptions: {
          from: Route.fullPath,
          to: "/campaigns/$id/delivery",
        },
        title: "Skift levering",
      }}
    >
      <div className="flex h-full flex-col">
        <div className="flex grow flex-col justify-center p-8 xl:p-14">
          {showSubStepper && stepper.visibleSteps.length ? (
            <AnimatePresence initial={false}>
              {!hideSubStepperSteps && (
                <motion.div
                  className="flex items-center justify-center"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <div className="flex items-center justify-center pb-8 xl:pb-14">
                    <HorizontalStepperSteps
                      stepper={stepper}
                      steps={stepper.visibleSteps}
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          ) : null}

          <div className="flex grow flex-col justify-center">
            <Outlet />
          </div>
        </div>

        {showBottomBar && (
          <StepperBottomBar
            stepper={stepper}
            hideSubmitButton={hideSubmitButton}
          />
        )}
      </div>
      <AlertModal
        isOpen={blocker.status === "blocked"}
        title="Du har ikke-gemte ændringer"
        description="Dine ændringer vil gå tabt. Er du sikker på at du vil fortsætte?"
        onClose={blocker.reset}
        onConfirm={() => {
          blocker.proceed?.();
        }}
      />

      <AlertModal
        isOpen={confirmState !== null}
        title="Skift levering"
        description="Er du sikker på at du vil skifte leveringsmetode?"
        confirmButtonText="Gem"
        onClose={() => {
          const resolve = confirmState?.resolve;
          resolve?.(false);
          setConfirmState(null);
        }}
        onConfirm={() => {
          const resolve = confirmState?.resolve;
          resolve?.(true);
        }}
      />
    </PageHeaderProvider>
  );
}
